import { useMemo } from 'react';
import { uniqBy } from 'es-toolkit';
import { useTranslation } from 'next-i18next';

import type { CategoryTreeBuildDto } from '@/api';

import {
  getSchoolTypeFromGradeIds,
  shouldExcludeSubject,
} from '../utils/subjectFilters';

/**
 * 선택된 학년 그룹에 따라 사용 가능한 과목 필터
 *
 * @param gradeGroup - 선택된 학년 그룹 (categoryTreeId 배열)
 * @param subjectTree - 과목 트리
 * @returns 과목 옵션 배열 { value: string, label: string }
 *
 * @example
 * // gradeGroup = ["10101", "10102"] (초등 1-2학년)
 * // Returns subjects that have "10101" or "10102" in their ancestors
 * const subjects = useAvailableSubjects(gradeGroup, subjectTree);
 */
export const useAvailableSubjects = (
  gradeGroup: string[] | null,
  subjectTree: CategoryTreeBuildDto[],
) => {
  const { t } = useTranslation(['build']);

  return useMemo(() => {
    const allSubject = {
      value: 'all',
      label: t('build.standard.subject.all') as string,
    };

    if (!gradeGroup || gradeGroup.length === 0) return [allSubject];

    // Determine school type for exclusion logic
    const schoolType = getSchoolTypeFromGradeIds(gradeGroup);

    // Filter: subjectTree에서 ancestors에 gradeGroup의 요소가 하나라도 포함된 것들
    const filtered = subjectTree.filter(subject =>
      gradeGroup.some(gradeId => subject.ancestors.includes(gradeId)),
    );

    // categoryGroupId로 unique하게 (es-toolkit의 uniqBy 사용)
    const uniqueSubjects = uniqBy(
      filtered,
      (item: CategoryTreeBuildDto) => item.categoryGroupId,
    );

    // Separate into included and excluded subjects
    const includedSubjects: CategoryTreeBuildDto[] = [];

    for (const subject of uniqueSubjects) {
      if (!shouldExcludeSubject(schoolType, subject.name)) {
        includedSubjects.push(subject);
      }
    }

    // Map included subjects to options
    const subjectOptions = includedSubjects.map(
      (item: CategoryTreeBuildDto) => ({
        value: item.categoryGroupId,
        label: item.name,
      }),
    );

    // Note: Excluded subjects' IDs are implicitly included in "all" option
    // because the search API will still process them when categoryIds uses grade-level IDs
    return [allSubject, ...subjectOptions];
  }, [gradeGroup, subjectTree, t]);
};
